import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  IconButton,
  Grid,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Lock as LockIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { userService } from '../services/api';

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialogs
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      // Get all users (both active and inactive) for management
      const response = await userService.getUsers({ is_active: 'all' });
      setUsers(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Filter users based on search
  const filteredUsers = users.filter((user) =>
    user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.role?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Open role change dialog
  const handleChangeRole = (user) => {
    setSelectedUser(user);
    setNewRole(user.role);
    setRoleDialogOpen(true);
    setError('');
  };

  // Confirm role change
  const confirmRoleChange = async () => {
    try {
      await userService.updateRole(selectedUser._id, newRole);
      setSuccess(`Role updated to ${newRole} for ${selectedUser.username}`);
      setRoleDialogOpen(false);
      loadUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  // Open password reset dialog
  const handleResetPassword = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setConfirmPassword('');
    setShowPassword(false);
    setPasswordDialogOpen(true);
    setError('');
  };

  // Confirm password reset
  const confirmPasswordReset = async () => {
    if (!newPassword || newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      await userService.resetPassword(selectedUser._id, newPassword);
      setSuccess(`Password reset successfully for ${selectedUser.username}`);
      setPasswordDialogOpen(false);
      setNewPassword('');
      setConfirmPassword('');
      setSelectedUser(null);
      setError('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to reset password');
    }
  };

  // Toggle user active status
  const handleToggleActive = async (user) => {
    const action = user.is_active ? 'deactivate' : 'activate';
    const confirmMessage = `Are you sure you want to ${action} user "${user.username}"?\n\n${
      user.is_active ? 'The user will not be able to login.' : 'The user will be able to login again.'
    }`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      if (user.is_active) {
        // Deactivate user (soft delete)
        await userService.updateUser(user._id, { is_active: false });
        setSuccess(`User ${user.username} deactivated successfully`);
      } else {
        // Activate user
        await userService.updateUser(user._id, { is_active: true });
        setSuccess(`User ${user.username} activated successfully`);
      }
      loadUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || `Failed to ${action} user`);
    }
  };

  const columns = [
    {
      field: 'username',
      headerName: 'Username',
      width: 130,
      renderCell: (params) => <strong>{params.value}</strong>,
    },
    {
      field: 'full_name',
      headerName: 'Full Name',
      width: 170,
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 180,
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params.value === 'admin' ? 'secondary' : 'primary'}
          size='small'
        />
      ),
    },
    {
      field: 'team',
      headerName: 'Team',
      width: 120,
      valueGetter: (params) => params.row.team?.name || 'Not assigned',
    },
    {
      field: 'meter_credit',
      headerName: 'Credit',
      width: 80,
      renderCell: (params) =>
        params.row.role === 'worker' ? (
          <Chip label={params.value} size='small' color='success' />
        ) : (
          <Typography variant='body2' color='text.secondary'>
            N/A
          </Typography>
        ),
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size='small'
        />
      ),
    },
    {
      field: 'last_login',
      headerName: 'Last Login',
      width: 150,
      valueGetter: (params) =>
        params.value ? new Date(params.value).toLocaleDateString() : 'Never',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 320,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button
            variant='outlined'
            size='small'
            startIcon={<EditIcon />}
            onClick={() => handleChangeRole(params.row)}
          >
            Role
          </Button>
          <Button
            variant='outlined'
            size='small'
            startIcon={<LockIcon />}
            onClick={() => handleResetPassword(params.row)}
          >
            Password
          </Button>
          <Button
            variant='outlined'
            size='small'
            color={params.row.is_active ? 'error' : 'success'}
            startIcon={params.row.is_active ? <BlockIcon /> : <CheckCircleIcon />}
            onClick={() => handleToggleActive(params.row)}
          >
            {params.row.is_active ? 'Deactivate' : 'Activate'}
          </Button>
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight='400px'>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display='flex' justifyContent='space-between' alignItems='center' mb={3}>
        <Typography variant='h4'>User Management</Typography>
      </Box>

      {error && (
        <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity='success' sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color='text.secondary' gutterBottom>
                Total Users
              </Typography>
              <Typography variant='h4'>{users.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color='text.secondary' gutterBottom>
                Admins
              </Typography>
              <Typography variant='h4' color='secondary.main'>
                {users.filter((u) => u.role === 'admin').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color='text.secondary' gutterBottom>
                Workers
              </Typography>
              <Typography variant='h4' color='primary.main'>
                {users.filter((u) => u.role === 'worker').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color='text.secondary' gutterBottom>
                Active Users
              </Typography>
              <Typography variant='h4' color='success.main'>
                {users.filter((u) => u.is_active).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          {/* Search Field */}
          <TextField
            fullWidth
            variant='outlined'
            placeholder='Search by username, full name, email, or role...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          {/* Users Table */}
          <DataGrid
            rows={filteredUsers}
            columns={columns}
            getRowId={(row) => row._id}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
            }}
            pageSizeOptions={[5, 10, 25, 50]}
            autoHeight
            disableRowSelectionOnClick
          />
        </CardContent>
      </Card>

      {/* Change Role Dialog */}
      <Dialog open={roleDialogOpen} onClose={() => setRoleDialogOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>Change User Role</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Typography variant='body2' color='text.secondary' paragraph sx={{ mt: 1 }}>
            User: <strong>{selectedUser?.username}</strong> ({selectedUser?.full_name})
          </Typography>
          <Typography variant='body2' color='text.secondary' paragraph>
            Current Role: <Chip label={selectedUser?.role} size='small' />
          </Typography>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>New Role</InputLabel>
            <Select
              value={newRole}
              label='New Role'
              onChange={(e) => setNewRole(e.target.value)}
            >
              <MenuItem value='admin'>Admin</MenuItem>
              <MenuItem value='worker'>Worker</MenuItem>
            </Select>
          </FormControl>
          <Alert severity='warning' sx={{ mt: 2 }}>
            <strong>Warning:</strong> Changing role will affect user permissions immediately.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmRoleChange} variant='contained'>
            Change Role
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={passwordDialogOpen} onClose={() => {
        setPasswordDialogOpen(false);
        setNewPassword('');
        setConfirmPassword('');
        setError('');
      }} maxWidth='sm' fullWidth>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          <Typography variant='body2' color='text.secondary' paragraph sx={{ mt: 1 }}>
            User: <strong>{selectedUser?.username}</strong> ({selectedUser?.full_name})
          </Typography>
          <TextField
            fullWidth
            margin='dense'
            label='New Password'
            type={showPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              setError(''); // Clear error when user types
            }}
            sx={{ mb: 2, mt: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge='end'>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            error={!!error && error.includes('Password')}
          />
          <TextField
            fullWidth
            margin='dense'
            label='Confirm Password'
            type={showPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              setError(''); // Clear error when user types
            }}
            error={!!error && error.includes('match')}
          />
          <Alert severity='info' sx={{ mt: 2 }}>
            Password must be at least 6 characters long
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setPasswordDialogOpen(false);
            setNewPassword('');
            setConfirmPassword('');
            setError('');
          }}>Cancel</Button>
          <Button 
            onClick={confirmPasswordReset} 
            variant='contained' 
            color='error'
            disabled={!newPassword || !confirmPassword || newPassword.length < 6}
          >
            Reset Password
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

